import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { ComponentType } from 'react';

export const NEW_RECORD_PILL_CY = 'vault-new-record-pill';

/** [MCV-1812] The small "New" mark next to the primary cell of a record a parse just created. */
export const NewRecordPill = () => (
  <span
    className="ml-2 shrink-0 rounded-full bg-primary-t-900 px-2 py-0.5 text-[11px] font-semibold leading-none text-primary-50"
    data-cy={NEW_RECORD_PILL_CY}
  >
    New
  </span>
);

// Columns that carry no record text: status icons, row actions, hidden ids.
const NON_PRIMARY_COL_IDS = new Set(['actions', 'delete', 'edit', 'validFlag', 'id']);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid ColDef is generic with any
type AnyColDef = ColDef<any, any>;

const isPrimaryCandidate = (col: AnyColDef): boolean =>
  typeof col.field === 'string' &&
  col.field !== '' &&
  !col.hide &&
  !col.pinned &&
  !col.checkboxSelection &&
  !(col.colId && NON_PRIMARY_COL_IDS.has(col.colId)) &&
  !(col.field && NON_PRIMARY_COL_IDS.has(col.field)) &&
  // A built-in renderer given by name cannot be wrapped.
  typeof col.cellRenderer !== 'string';

/**
 * [MCV-1812] Adds the "New" pill to the primary column of a vault grid: the first visible column
 * with record text (the status icon, row actions and hidden id are skipped). Rows whose `id` is in
 * `newIds` render the pill after the column's own content, whatever renderer it already had.
 *
 * Returns `colDefs` untouched when nothing is new, so a grid with no marks is not re-created.
 */
export const withNewRecordPill = <T extends AnyColDef>(
  colDefs: T[],
  newIds: ReadonlySet<number>
): T[] => {
  if (newIds.size === 0) return colDefs;
  const primaryIndex = colDefs.findIndex(isPrimaryCandidate);
  if (primaryIndex === -1) return colDefs;

  const primary = colDefs[primaryIndex];
  const Inner = primary.cellRenderer as ComponentType<ICellRendererParams> | undefined;
  const WithPill = (params: ICellRendererParams) => {
    const id = params.data?.id;
    const isNew = id != null && newIds.has(Number(id));
    const content = Inner ? (
      <Inner {...params} />
    ) : (
      <span className="truncate">{String(params.valueFormatted ?? params.value ?? '')}</span>
    );
    if (!isNew) return content;
    return (
      <span className="flex min-w-0 items-center">
        {content}
        <NewRecordPill />
      </span>
    );
  };

  const next = [...colDefs];
  next[primaryIndex] = { ...primary, cellRenderer: WithPill };
  return next;
};
